import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { ServiceCenter } from '@xpo-ltl-2.0/sdk-location';
import { first as _first, get as _get, size as _size } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { XpoLtlServiceCentersService } from '../services/service-centers/service-centers.service';
import { XpoLtlSicSwitcherLabelOptions } from './sic-switcher-label-options';

@Component({
  selector: 'xpo-ltl-sic-switcher',
  templateUrl: './sic-switcher.component.html',
  styleUrls: ['./sic-switcher.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XpoLtlSicSwitcherComponent implements OnInit, AfterViewInit {
  @ViewChild(MatInput) sicInput: MatInput;
  @ViewChild(MatAutocomplete) sicAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) sicAutocompleteTrigger: MatAutocompleteTrigger;

  get sicCd(): string {
    return _get(this.selectedSic, 'sicCd', '');
  }
  @Input()
  set sicCd(value: string) {
    this.selectSicWithCd(value);
  }

  @Input() panelWidth: string = 'auto';

  @Input() sicLabel: XpoLtlSicSwitcherLabelOptions = new XpoLtlSicSwitcherLabelOptions();

  @Output() sicChange = new EventEmitter<ServiceCenter>();

  private selectedSic: ServiceCenter;
  private previousSic: ServiceCenter;
  private previousFilterTerm = undefined;

  private readonly filteredSicListSubject = new BehaviorSubject<ServiceCenter[]>([]);
  readonly filteredSicList$ = this.filteredSicListSubject.asObservable();

  constructor(private serviceCentersService: XpoLtlServiceCentersService) {}

  ngOnInit() {
    this.updateFilteredSics('');
  }

  ngAfterViewInit(): void {
    // ensure that the input is set to the initial value, in case it was set before the input was created
    setTimeout(() => {
      this.sicInput.value = this.getSicDesc(this.selectedSic);
    });
  }

  // #region PUBLIC API

  /**
   * Set the focus to the input control
   */
  focus() {
    this.sicInput.focus();
  }

  // #endregion PUBLIC AIP

  // #region PRIVATE API

  handleFilterFocus() {
    this.previousSic = this.selectedSic;
  }

  handleFilterBlur() {
    if (!this.sicAutocomplete.isOpen) {
      this.selectSicFromFilter();
    }
  }

  handlePanelClosed() {
    if (this.sicInput.value !== _get(this.selectedSic, 'sicCd')) {
      this.selectSic(this.selectedSic);
    }
  }

  /**
   * Update the list of ServiceCenters to contain the list that matches the serch term.
   * @param filterTerm term to filter ServiceCenters by
   */
  updateFilteredSics(filterTerm: string) {
    if (filterTerm !== this.previousFilterTerm) {
      this.previousFilterTerm = filterTerm;
      this.serviceCentersService.serviceCenters$
        .pipe(
          take(1),
          map((sics: ServiceCenter[]) => this.serviceCentersService.filterSicsBy(sics, filterTerm)),
          tap((serviceCenters: ServiceCenter[]) => {
            this.filteredSicListSubject.next(serviceCenters);
          })
        )
        .subscribe();
    }
  }

  displayFn(sic: ServiceCenter): string {
    return sic?.sicCd ?? undefined;
  }

  /**
   * Select the passed ServiceCenter and update UI to reflect it
   * @param newSic ServiceCenter to select
   */
  selectSic(newSic: ServiceCenter) {
    if (this.selectedSic !== newSic) {
      // only broadcast event if the new sic is different from previous sic
      this.sicChange.emit(newSic);
      this.selectedSic = newSic;
      this.previousSic = newSic;
    }

    // reset the filterTerm to be the selected SicCd
    const sicDesc = this.getSicDesc(this.selectedSic);
    if (this.sicInput) {
      this.sicInput.value = sicDesc;
    }
    this.updateFilteredSics(sicDesc);
  }

  private getSicDesc(sic: ServiceCenter): string {
    return sic ? `${sic.sicCd} - ${sic.reference.sicName}` : '';
  }

  /**
   * Select a ServiceCenter that has the passed sicCd
   *
   * @param sicCd sicCd for the ServiceCenter to select
   */
  private selectSicWithCd(sicCd: string) {
    this.serviceCentersService
      .getSicByCd$(sicCd)
      .pipe(take(1))
      .subscribe((sic: ServiceCenter) => {
        this.selectSic(sic);
      });
  }

  /**
   * If only one sic in the fitler list, select it. Else, clear the selection
   */
  private selectSicFromFilter() {
    this.filteredSicList$.pipe(take(1)).subscribe((filteredSics: ServiceCenter[]) => {
      if (_size(filteredSics) === 1) {
        // auto-select the only option
        this.selectSic(_first(filteredSics));
      } else {
        // there are more then one option, so can't auto-select from filter list
        this.selectSic(this.previousSic);
      }
    });
  }

  // #endregion
}
